import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Layers, ListChecks, X } from "lucide-react";
import { AdminModules } from "./AdminModules";
import { AdminQuestions } from "./AdminQuestions";

const BANDS = ["foundation", "builder", "innovator"];
const BIG_IDEAS = ["senses", "thinks", "learns", "talks", "changes"];

const EMPTY_COURSE = {
  band: "foundation",
  title: { en: "", ar: "" },
  description: { en: "", ar: "" },
  big_idea: "senses",
  difficulty: 1,
  cover_emoji: "✨",
  cover_gradient: "linear-gradient(135deg,#4CC9F0,#4361EE)",
  free_module_count: 5,
  paid_module_count: 14,
  has_exam: true,
  status: "draft",
};

export default function AdminCourses() {
  const { t, lang } = useI18n();
  const [courses, setCourses] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // course being edited or null for create
  const [drawer, setDrawer] = useState(null); // { kind: 'modules' | 'questions', course }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setCourses(null);
    try {
      const rows = await api.adminListCourses();
      setCourses(rows);
    } catch (e) {
      toast.error(e.message);
      setCourses([]);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing({ ...EMPTY_COURSE }); setEditorOpen(true); };
  const openEdit = (c) => {
    setEditing({
      course_id: c.course_id,
      band: c.band, big_idea: c.big_idea, difficulty: c.difficulty,
      title: { ...(c.title || {}) }, description: { ...(c.description || {}) },
      cover_emoji: c.cover_emoji, cover_gradient: c.cover_gradient,
      status: c.status || "published",
    });
    setEditorOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.course_id) {
        const { course_id, ...body } = editing;
        await api.adminPatchCourse(course_id, body);
        toast.success(t("admin.updated"));
      } else {
        await api.adminCreateCourse(editing);
        toast.success(t("admin.created"));
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try { await api.adminDeleteCourse(c.course_id); toast.success(t("admin.updated")); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#4361EE]">{t("admin.title")}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">{t("admin.courses")}</h1>
        </div>
        <Button
          onClick={openCreate}
          className="rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 text-white font-bold px-5 shadow-[0_3px_0_#2A3EB1]"
          data-testid="admin-new-course-btn"
        >
          <Plus className="h-4 w-4 me-1" /> {t("admin.newCourse")}
        </Button>
      </header>

      {courses === null ? (
        <div className="text-black/50">{t("superadmin.loading")}</div>
      ) : courses.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-black/10 p-10 text-center text-black/50">
          {t("superadmin.empty")}
        </div>
      ) : (
        <div className="grid gap-4">
          {courses.map((c) => (
            <div
              key={c.course_id}
              className="rounded-2xl border border-black/5 bg-white shadow-[0_2px_0_rgba(0,0,0,0.04)] p-5 flex items-center gap-4 flex-wrap"
              data-testid={`admin-course-${c.course_id}`}
            >
              <div
                className="h-14 w-14 rounded-2xl grid place-items-center text-2xl"
                style={{ background: c.cover_gradient }}
              >
                {c.cover_emoji || "✨"}
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-black text-lg">{c.title?.[lang] || c.title?.en}</div>
                <div className="text-xs text-black/60 mt-0.5">{c.course_id}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <Badge variant="secondary" className="text-[10px]">{c.band}</Badge>
                  <Badge variant="secondary" className="text-[10px]">{c.big_idea}</Badge>
                  <Badge
                    className={`text-[10px] ${
                      c.status === "published"
                        ? "bg-[#4CC9F0]/20 text-[#0A5566]"
                        : "bg-[#FFB703]/20 text-[#7A4B00]"
                    }`}
                  >
                    {c.status === "published" ? t("admin.published") : t("admin.draft")}
                  </Badge>
                  <span className="text-xs text-black/50 ms-1">
                    {c.enrollment_count} enrolls · {c.module_count_live} modules
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDrawer({ kind: "modules", course: c })}
                  className="rounded-full font-bold"
                  data-testid={`admin-edit-modules-${c.course_id}`}
                >
                  <Layers className="h-4 w-4 me-1" /> {t("admin.editModules")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDrawer({ kind: "questions", course: c })}
                  className="rounded-full font-bold"
                  data-testid={`admin-edit-questions-${c.course_id}`}
                >
                  <ListChecks className="h-4 w-4 me-1" /> {t("admin.editQuestions")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => openEdit(c)}
                  className="rounded-full font-bold"
                  data-testid={`admin-edit-course-${c.course_id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => del(c)}
                  className="rounded-full font-bold text-[#D90429] hover:bg-[#D90429]/10"
                  data-testid={`admin-delete-course-${c.course_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editorOpen && editing && (
        <CourseEditor
          value={editing}
          onChange={setEditing}
          onCancel={() => setEditorOpen(false)}
          onSave={save}
          saving={saving}
          t={t}
        />
      )}

      {drawer && (
        <Drawer onClose={() => setDrawer(null)} title={`${drawer.course.title?.en} · ${drawer.kind === "modules" ? t("admin.modules") : t("admin.questions")}`}>
          {drawer.kind === "modules" ? (
            <AdminModules course={drawer.course} />
          ) : (
            <AdminQuestions course={drawer.course} />
          )}
        </Drawer>
      )}
    </div>
  );
}

function Drawer({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-[#FDFBF7] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-black/5 p-4 flex items-center justify-between z-10">
          <h2 className="font-black text-lg">{title}</h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-black/5" data-testid="admin-drawer-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 md:p-6">{children}</div>
      </div>
    </div>
  );
}

function CourseEditor({ value, onChange, onCancel, onSave, saving, t }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const setBi = (field, side, v) => onChange({ ...value, [field]: { ...value[field], [side]: v } });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8 bg-black/40" data-testid="admin-course-editor">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 md:p-8 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-black text-xl">{value.course_id ? t("admin.updated") : t("admin.newCourse")}</h2>
          <button onClick={onCancel} className="rounded-full p-2 hover:bg-black/5">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label={t("admin.titleEn")}>
            <Input value={value.title.en} onChange={(e) => setBi("title", "en", e.target.value)}
                   data-testid="admin-course-title-en" />
          </Field>
          <Field label={t("admin.titleAr")}>
            <Input dir="rtl" value={value.title.ar} onChange={(e) => setBi("title", "ar", e.target.value)}
                   data-testid="admin-course-title-ar" />
          </Field>
          <Field label={t("admin.descEn")}>
            <Textarea rows={3} value={value.description.en}
                      onChange={(e) => setBi("description", "en", e.target.value)}
                      data-testid="admin-course-desc-en" />
          </Field>
          <Field label={t("admin.descAr")}>
            <Textarea dir="rtl" rows={3} value={value.description.ar}
                      onChange={(e) => setBi("description", "ar", e.target.value)}
                      data-testid="admin-course-desc-ar" />
          </Field>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label={t("admin.band")}>
            <select value={value.band} onChange={(e) => set({ band: e.target.value })}
                    className="w-full rounded-md border border-black/10 h-10 px-3 font-semibold bg-white"
                    data-testid="admin-course-band">
              {BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label={t("admin.bigIdea")}>
            <select value={value.big_idea} onChange={(e) => set({ big_idea: e.target.value })}
                    className="w-full rounded-md border border-black/10 h-10 px-3 font-semibold bg-white"
                    data-testid="admin-course-big-idea">
              {BIG_IDEAS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label={t("admin.difficulty")}>
            <Input type="number" min={1} max={5} value={value.difficulty}
                   onChange={(e) => set({ difficulty: parseInt(e.target.value || "1", 10) })}
                   data-testid="admin-course-difficulty" />
          </Field>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 font-semibold text-sm">
            <input
              type="checkbox"
              checked={value.status === "published"}
              onChange={(e) => set({ status: e.target.checked ? "published" : "draft" })}
              data-testid="admin-course-published-toggle"
            />
            {t("admin.publish")}
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onCancel} className="rounded-full font-bold" data-testid="admin-course-cancel">
            {t("admin.cancel")}
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !value.title.en || !value.title.ar}
            className="rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 text-white font-bold px-6 shadow-[0_3px_0_#2A3EB1]"
            data-testid="admin-course-save"
          >
            {saving ? "…" : t("admin.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold text-black/70 uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
